import { useTagManager } from '@hooks'
import { useCurrentShopQuery } from '@modules/Dashboard/hooks/useCurrentShopQuery'
import { useTiktokIntegrationQuery } from '@queries'

import logoSrc from '../../../assets/signets/tiktok.svg'
import type { ConnectBarProps } from '../../../components/ConnectBar/ConnectBar'
import { useAppConnectEvent } from '../../../hooks/useAppConnectEvent'
import { useOAuthDialog } from '../../../hooks/useOAuthDialog'
import type { FormValues } from '../AppsTiktok.types'
import { useCreateTiktokIntegrationMutation } from '../queries/useCreateTiktokIntegrationMutation'
import { useRemoveTiktokIntegrationMutation } from '../queries/useRemoveTiktokIntegrationMutation'
import { useUpdateTiktokIntegrationMutation } from '../queries/useUpdateTiktokIntegrationMutation'

export const useTiktok = () => {
  const { currentShopQuery } = useCurrentShopQuery()
  const { createTiktokIntegrationMutation } = useCreateTiktokIntegrationMutation()
  const { removeTiktokIntegrationMutation } = useRemoveTiktokIntegrationMutation()
  const { updateTiktokIntegrationMutation } = useUpdateTiktokIntegrationMutation()
  const { tiktokIntegrationQuery, setTiktokIntegrationData } = useTiktokIntegrationQuery(
    [currentShopQuery.data?.id!],
    { enabled: !!currentShopQuery.data?.id, onError: undefined },
  )
  const { openOAuthDialog } = useOAuthDialog({
    onComplete: () => {
      tiktokIntegrationQuery.refetch()
      tagManager.trigger('app_connected', { app_name: 'tiktok_feed' })
    },
  })
  const tagManager = useTagManager()
  const appConnectEvent = useAppConnectEvent('tiktok_feed')

  const getStatus = (): 'NOT_CONNECTED' | 'CONNECTED' | undefined => {
    if (tiktokIntegrationQuery.isError) {
      return 'NOT_CONNECTED'
    }

    if (tiktokIntegrationQuery.data?.consumerToken) {
      return 'CONNECTED'
    }
  }

  const status = getStatus()

  const getConnectBar = (): ConnectBarProps => {
    const connectBarBase = {
      logoSrc,
      description: 'Connect to your TikTok account',
      disabled: true,
    }

    if (status === 'CONNECTED') {
      const onDisconnect = async () => {
        await removeTiktokIntegrationMutation.mutateAsync([currentShopQuery.data?.id!])
        await tiktokIntegrationQuery.refetch()
      }

      return {
        ...connectBarBase,
        disabled: false,
        onDisconnect,
      }
    }

    if (status === 'NOT_CONNECTED') {
      const onConnect = async () => {
        appConnectEvent.trigger()

        const integration = await createTiktokIntegrationMutation.mutateAsync([
          currentShopQuery.data?.id!,
          { flow: 'postMessage' },
        ])

        openOAuthDialog(integration.redirectTo)
      }

      return {
        ...connectBarBase,
        disabled: false,
        onConnect,
      }
    }

    return connectBarBase
  }

  const handleSettingsSubmit = (values: FormValues) => {
    updateTiktokIntegrationMutation.mutate(
      [currentShopQuery.data?.id!, { features: { kudos_sync: values.kudosSync } }],
      {
        onSuccess: (data) => {
          setTiktokIntegrationData(data)
        },
      },
    )
  }

  return {
    tiktok: {
      connectBar: getConnectBar(),
      settings: {
        onSubmit: handleSettingsSubmit,
        isSubmitting: updateTiktokIntegrationMutation.isLoading,
        initialValues: {
          kudosSync: !!tiktokIntegrationQuery.data?.consumerToken?.features.kudosSync,
        },
      },
      status,
    },
  }
}
